// JSON Converter for parsing and normalizing JSON data

import { JsonArray, JsonObject, JsonValue, JsonValueType } from './json/jsonValue.js';
import { SensitiveDataRedactor } from '@arshu/common';
import { Logger } from '../../../Arshu/dist/logger/logger.js';

type PlainValue = string | number | boolean | null | PlainValue[] | { [key: string]: PlainValue };

export class JsonUtility {
    static redactSensitive(obj: JsonObject): JsonObject;
    static redactSensitive(value: JsonValue): JsonValue;
    static redactSensitive(value: JsonObject | JsonValue): JsonObject | JsonValue {
        if (value instanceof JsonObject) {
            const redacted = new JsonObject();
            for (const [key, item] of value) {
                redacted.set(key, SensitiveDataRedactor.isSensitiveKey(key)
                    ? JsonValue.string(SensitiveDataRedactor.redactedValue)
                    : JsonUtility.redactSensitive(item));
            }
            return redacted;
        }
        if (value.isString) return JsonValue.string(SensitiveDataRedactor.redactText(value.getString()));
        if (value.isObject) return JsonValue.object(JsonUtility.redactSensitive(value.getObject()));
        if (value.isArray) {
            const redacted = new JsonArray();
            for (const item of value.getArray()) redacted.add(JsonUtility.redactSensitive(item));
            return JsonValue.array(redacted);
        }
        return value.deepClone();
    }

    static getSensitiveStringValues(obj: JsonObject | null): string[] {
        const values: string[] = [];
        if (obj !== null) JsonUtility.collectSensitiveStringValues(obj, values);
        return values;
    }

    private static collectSensitiveStringValues(obj: JsonObject, values: string[]): void {
        for (const [key, value] of obj) {
            if (SensitiveDataRedactor.isSensitiveKey(key)) {
                JsonUtility.collectStringScalars(value, values);
            } else if (value.isObject) {
                JsonUtility.collectSensitiveStringValues(value.getObject(), values);
            } else if (value.isArray) {
                for (const item of value.getArray()) {
                    if (item.isObject) JsonUtility.collectSensitiveStringValues(item.getObject(), values);
                }
            }
        }
    }

    private static collectStringScalars(value: JsonValue, values: string[]): void {
        if (value.isString) {
            const raw = value.getString();
            if (raw && !values.includes(raw)) values.push(raw);
        } else if (value.isArray) {
            for (const item of value.getArray()) JsonUtility.collectStringScalars(item, values);
        } else if (value.isObject) {
            for (const [, item] of value.getObject()) JsonUtility.collectStringScalars(item, values);
        }
    }

    static redactSensitiveInPlace(obj: JsonObject): void {
        for (const [key, value] of [...obj]) {
            obj.set(key, SensitiveDataRedactor.isSensitiveKey(key)
                ? JsonValue.string(SensitiveDataRedactor.redactedValue)
                : JsonUtility.redactSensitive(value));
        }
    }

    static serializeRedacted(obj: JsonObject, indented: boolean = false): string {
        return JsonUtility.serializeObject(JsonUtility.redactSensitive(obj), indented);
    }

    static convertJsonNode(node: unknown): JsonValue {
        if (node === null || node === undefined) {
            return JsonValue.null();
        }

        if (typeof node === 'string') {
            return JsonValue.string(node);
        }

        if (typeof node === 'number') {
            if (Number.isInteger(node)) {
                return JsonValue.integer(node);
            }
            return JsonValue.number(node);
        }

        if (typeof node === 'boolean') {
            return JsonValue.bool(node);
        }

        if (Array.isArray(node)) {
            const customArray = new JsonArray();
            for (const item of node) {
                customArray.add(JsonUtility.convertJsonNode(item));
            }
            return JsonValue.array(customArray);
        }

        if (typeof node === 'object') {
            const customObject = new JsonObject();
            for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
                customObject.add(key, JsonUtility.convertJsonNode(value));
            }
            return JsonValue.object(customObject);
        }

        return JsonValue.string(String(node));
    }

    static parseJsonString(jsonContent: string | null | undefined): JsonObject {
        try {
            if (!jsonContent || typeof jsonContent !== 'string') {
                return new JsonObject();
            }

            const parsed = JSON.parse(jsonContent) as unknown;
            return JsonUtility.normalizeJsonObject(parsed);
        } catch {
            return new JsonObject();
        }
    }

    /**
     * Decodes bytes that MUST already be valid UTF-8 — a supplied artifact, not an optional input.
     * The default TextDecoder rewrites invalid bytes to U+FFFD, so corrupt content reads back as
     * plausible text; `fatal: true` raises instead, keeping the corruption at its source.
     */
    static decodeUtf8Strict(bytes: Uint8Array): string {
        return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    }

    /**
     * Parses JSON that MUST be valid — a supplied artifact, not an optional input.
     * parseJsonString returns an EMPTY JsonObject on failure, and a caller holding real bytes cannot
     * distinguish that from "the input was absent"; sharing one sentinel across both is what lets a
     * corrupt artifact silently degrade into a recomputed value. This entry point raises instead.
     */
    static parseJsonStringStrict(jsonContent: string): JsonObject {
        // No try/catch — JSON.parse raising on malformed input is the entire point of this overload.
        const parsed = JSON.parse(jsonContent) as unknown;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            throw new Error('Expected a JSON object at the root of the parsed content.');
        }
        return JsonUtility.normalizeJsonObject(parsed);
    }

    static normalizeJsonObject(obj: unknown): JsonObject {
        if (!obj || typeof obj !== 'object') {
            return new JsonObject();
        }

        if (Array.isArray(obj)) {
            return JsonUtility.normalizeJsonArray(obj) as unknown as JsonObject;
        }

        const jsonObject = new JsonObject();

        for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
            const convertedValue: JsonValue = JsonUtility.convertJsonNode(value);
            jsonObject.set(key, convertedValue);
        }

        return jsonObject;
    }

    static normalizeJsonArray(arr: unknown): JsonArray {
        if (!Array.isArray(arr)) {
            return new JsonArray();
        }

        const jsonArray = new JsonArray();

        for (const item of arr) {
            const convertedValue: JsonValue = JsonUtility.convertJsonNode(item);
            jsonArray.push(convertedValue);
        }

        return jsonArray;
    }

    /**
     * Converts a raw parsed-JSON value (string / number / boolean / null / array / object)
     * to the Arshu JsonValue model. Recursive: preserves nested arrays and objects.
     * This is the shared HTTP boundary converter — endpoints must not reimplement.
     * Mirrors csharp JsonUtility.FromJsonElement.
     */
    static fromJsonElement(value: unknown): JsonValue {
        return JsonUtility.parseJsonElement(value);
    }

    /**
     * Converts an optional raw value to JsonObject? for HTTP boundary use.
     * Returns null when value is missing, null, or not a plain object — callers typically
     * use this to extract an optional config/payload object from a request body.
     * Mirrors csharp JsonUtility.FromJsonElementObject.
     */
    static fromJsonElementObject(value: unknown | null): JsonObject | null {
        if (value === null || value === undefined) return null;
        if (typeof value !== 'object' || Array.isArray(value)) return null;
        const obj = new JsonObject();
        JsonUtility.parseJsonElementToJsonObject(value, obj);
        return obj;
    }

    private static parseJsonElementToJsonObject(element: unknown, target: JsonObject): void {
        if (element && typeof element === 'object' && !Array.isArray(element)) {
            for (const [key, value] of Object.entries(element as Record<string, unknown>)) {
                target.set(key, JsonUtility.parseJsonElement(value));
            }
        }
    }

    static toPlainObject(value: unknown): unknown {
        if (value instanceof JsonObject) {
            return value.toObject();
        }

        if (value instanceof JsonArray) {
            return value.toArray();
        }

        if (Array.isArray(value)) {
            return value.map(item => JsonUtility.toPlainObject(item));
        }

        if (value && typeof value === 'object') {
            const result: Record<string, unknown> = {};
            for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
                result[key] = JsonUtility.toPlainObject(val);
            }
            return result;
        }

        return value;
    }

    static serializeObject(obj: unknown, indented: boolean = false): string {
        try {
            if (obj === null || obj === undefined) {
                return 'null';
            }

            if (obj instanceof JsonValue) {
                return JsonUtility.#serializeJsonValue(obj, indented);
            }

            if (obj instanceof JsonObject) {
                return JsonUtility.#serializeJsonObject(obj, indented);
            }

            if (obj instanceof JsonArray) {
                return JsonUtility.#serializeJsonArray(obj, indented);
            }

            if (obj instanceof Map) {
                return JsonUtility.#serializeMap(obj as Map<string, unknown>, indented);
            }

            if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
                return JsonUtility.#serializePlainObject(obj as Record<string, unknown>, indented);
            }

            if (Array.isArray(obj)) {
                return JsonUtility.#serializeArray(obj, indented);
            }

            return JsonUtility.#serializePrimitive(obj);
        } catch {
            return '{}';
        }
    }

    static #serializeJsonValue(jsonValue: JsonValue, indented: boolean): string {
        switch (jsonValue.typeName) {
            case JsonValueType.String:
                return '"' + JsonUtility.internalEscapeJsonString(jsonValue.asString()!) + '"';
            case JsonValueType.Number:
                return jsonValue.asNumber()!.toString();
            case JsonValueType.Integer:
                return jsonValue.asInteger()!.toString();
            case JsonValueType.Bool:
                return jsonValue.asBool() ? 'true' : 'false';
            case JsonValueType.Array:
                return JsonUtility.serializeObject(jsonValue.asArray(), indented);
            case JsonValueType.Object:
                return JsonUtility.serializeObject(jsonValue.asObject(), indented);
            case JsonValueType.Null:
                return 'null';
            default:
                return 'null';
        }
    }

    static #serializeJsonObject(jsonObj: JsonObject, indented: boolean): string {
        const items: string[] = [];
        for (const [key, value] of jsonObj.getEnumerator()) {
            const keyJson: string = JsonUtility.internalEscapeJsonString(key);
            const valueJson: string = JsonUtility.#serializeJsonValue(value, false);
            items.push(`"${keyJson}":${valueJson}`);
        }
        const objectJson = '{' + items.join(',') + '}';
        return indented ? JsonUtility.#formatJson(objectJson) : objectJson;
    }

    static #serializeJsonArray(jsonArr: JsonArray, indented: boolean): string {
        const items: string[] = [];
        for (const item of jsonArr.getEnumerator()) {
            items.push(JsonUtility.#serializeJsonValue(item, false));
        }
        const arrayJson = '[' + items.join(',') + ']';
        return indented ? JsonUtility.#formatJson(arrayJson) : arrayJson;
    }

    static #serializeMap(map: Map<string, unknown>, indented: boolean): string {
        const items: string[] = [];
        for (const [key, value] of map.entries()) {
            const serializedValue = JsonUtility.serializeObject(value, false);
            const escapedKey = JsonUtility.internalEscapeJsonString(String(key));
            items.push(`"${escapedKey}":${serializedValue}`);
        }
        const objectJson = '{' + items.join(',') + '}';
        return indented ? JsonUtility.#formatJson(objectJson) : objectJson;
    }

    static #serializePlainObject(obj: Record<string, unknown>, indented: boolean): string {
        const items: string[] = [];
        for (const [key, value] of Object.entries(obj)) {
            const serializedValue = JsonUtility.serializeObject(value, false);
            const escapedKey = JsonUtility.internalEscapeJsonString(key);
            items.push(`"${escapedKey}":${serializedValue}`);
        }
        const objectJson = '{' + items.join(',') + '}';
        return indented ? JsonUtility.#formatJson(objectJson) : objectJson;
    }

    static #serializeArray(arr: unknown[], indented: boolean): string {
        const items = arr.map(item => JsonUtility.serializeObject(item, false));
        const arrayJson = '[' + items.join(',') + ']';
        return indented ? JsonUtility.#formatJson(arrayJson) : arrayJson;
    }

    static #serializePrimitive(value: unknown): string {
        if (typeof value === 'string') {
            return '"' + JsonUtility.internalEscapeJsonString(value) + '"';
        }
        if (typeof value === 'boolean') {
            return value.toString();
        }
        if (typeof value === 'number') {
            return value.toString();
        }
        return 'null';
    }

    static escapeJson(text: unknown): string {
        if (!text) return (text as string) || '';

        let str: string;
        if (text instanceof JsonValue) {
            str = text.toString();
        } else if (typeof text !== 'string') {
            str = String(text);
        } else {
            str = text;
        }

        return str
            .replace(/\\/g, '\\\\')
            .replace(/"/g, '\\u0022')
            .replace(/\n/g, '\\n')
            .replace(/\r/g, '\\r')
            .replace(/\t/g, '\\t')
            .replace(/[\b]/g, '\\b')
            .replace(/\f/g, '\\f');
    }

    static escapeJsonString(input: unknown): string {
        if (!input) return '';

        let str: string;
        if (input instanceof JsonValue) {
            str = input.toString();
        } else if (typeof input !== 'string') {
            str = String(input);
        } else {
            str = input;
        }

        return str
            .replace(/\\/g, '\\\\')
            .replace(/"/g, '\\"')
            .replace(/\r/g, '\\r')
            .replace(/\n/g, '\\n')
            .replace(/\t/g, '\\t')
            .replace(/</g, '\\u003C')
            .replace(/>/g, '\\u003E')
            .replace(/&/g, '\\u0026')
            .replace(/'/g, '\\u0027')
            .replace(/\+/g, '\\u002B');
    }

    static escapeHtmlString(input: unknown): string {
        if (!input) return '';

        let str: string;
        if (input instanceof JsonValue) {
            str = input.toString();
        } else if (typeof input !== 'string') {
            str = String(input);
        } else {
            str = input;
        }

        return str
            .replace(/\\/g, '\\\\')
            .replace(/"/g, '\\"')
            .replace(/\r/g, '\\r')
            .replace(/\n/g, '\\n')
            .replace(/\t/g, '\\t');
    }

    private static parseJsonElement(value: unknown): JsonValue {
        if (value === null || value === undefined) {
            return JsonValue.null();
        }

        if (typeof value === 'string') {
            return JsonValue.string(value);
        }

        if (typeof value === 'number') {
            // Preserve integer types
            if (Number.isInteger(value)) {
                return JsonValue.integer(value);
            }
            if (Number.isFinite(value)) {
                return JsonValue.number(value);
            }
            Logger.fallback("F-COE4",
                `JSON number out of int/long/double range: rawText=${value}`);
            throw new Error(`JSON number is outside the supported numeric range: ${value}`);
        }

        if (typeof value === 'boolean') {
            return JsonValue.bool(value);
        }

        if (Array.isArray(value)) {
            return JsonValue.array(JsonUtility.parseJsonArrayToJsonArray(value));
        }

        if (typeof value === 'object') {
            const nestedObj = new JsonObject();
            JsonUtility.parseJsonElementToJsonObject(value, nestedObj);
            return JsonValue.object(nestedObj);
        }

        return JsonValue.null();
    }

    private static parseJsonArrayToJsonArray(arrayElement: unknown): JsonArray {
        const result = new JsonArray();

        if (!Array.isArray(arrayElement)) {
            return result;
        }

        for (const item of arrayElement) {
            result.add(JsonUtility.parseJsonElement(item));
        }

        return result;
    }

    private static internalEscapeJsonString(str: string): string {
        return str
            .replace(/\\/g, '\\\\')
            .replace(/"/g, '\\u0022')
            .replace(/\r/g, '\\r')
            .replace(/\n/g, '\\n')
            .replace(/\t/g, '\\t');
    }

    static #formatJson(json: string): string {
        let indent = 0;
        let result = '';
        let inString = false;
        let escapeNext = false;

        for (let i = 0; i < json.length; i++) {
            const ch = json[i];

            if (escapeNext) {
                result += ch;
                escapeNext = false;
                continue;
            }

            if (ch === '\\' && inString) {
                result += ch;
                escapeNext = true;
                continue;
            }

            if (ch === '"') {
                inString = !inString;
                result += ch;
                continue;
            }

            if (inString) {
                result += ch;
                continue;
            }

            switch (ch) {
                case '{':
                case '[':
                    if (i + 1 < json.length && ((ch === '{' && json[i + 1] === '}') || (ch === '[' && json[i + 1] === ']'))) {
                        result += ch;
                    } else {
                        result += ch;
                        result += '\n';
                        indent++;
                        result += '  '.repeat(indent);
                    }
                    break;

                case '}':
                case ']':
                    if (result.length > 0 && (result[result.length - 1] === '{' || result[result.length - 1] === '[')) {
                        result += ch;
                    } else {
                        result += '\n';
                        indent--;
                        result += '  '.repeat(indent);
                        result += ch;
                    }
                    break;

                case ',':
                    result += ch;
                    result += '\n';
                    result += '  '.repeat(indent);
                    break;

                case ':':
                    result += ch;
                    result += ' ';
                    break;

                case ' ':
                case '\t':
                case '\r':
                case '\n':
                    break;

                default:
                    result += ch;
                    break;
            }
        }

        return result;
    }

    static parseJsonToJsonObject(jsonContent: string | null | undefined): Record<string, PlainValue> {
        const result: Record<string, PlainValue> = {};

        if (!jsonContent || typeof jsonContent !== 'string') {
            return result;
        }

        try {
            const parsed = JSON.parse(jsonContent) as unknown;
            JsonUtility.#parseJsonElementToJsonObject(parsed, result);
        } catch {
            return result;
        }

        return result;
    }

    static #parseJsonElementToJsonObject(element: unknown, target: Record<string, PlainValue>): void {
        if (!element || typeof element !== 'object' || Array.isArray(element)) {
            return;
        }

        for (const [key, value] of Object.entries(element as Record<string, unknown>)) {
            target[key] = JsonUtility.#parseJsonValue(value);
        }
    }

    static #parseJsonValue(value: unknown): PlainValue {
        if (value === null || value === undefined) {
            return null;
        }

        if (typeof value === 'string') {
            return value;
        }

        if (typeof value === 'number') {
            return value;
        }

        if (typeof value === 'boolean') {
            return value;
        }

        if (Array.isArray(value)) {
            return JsonUtility.#parseJsonArrayToJsonArray(value);
        }

        if (typeof value === 'object') {
            const nestedObj: Record<string, PlainValue> = {};
            JsonUtility.#parseJsonElementToJsonObject(value, nestedObj);
            return nestedObj;
        }

        return null;
    }

    static #parseJsonArrayToJsonArray(arrayElement: unknown): PlainValue[] {
        const result: PlainValue[] = [];

        if (!Array.isArray(arrayElement)) {
            return result;
        }

        for (const item of arrayElement) {
            result.push(JsonUtility.#parseJsonValue(item));
        }

        return result;
    }

}
